import React from 'react';
import {
    Box,
    Typography,
    Checkbox,
    Avatar,
    Chip,
    IconButton,
    Card,
    CardContent,
    Tooltip,
} from '@mui/material';
import {
    Star,
    StarBorder,
    DragIndicator,
    MoreVert,
    ChatBubbleOutline,
    Person,
} from '@mui/icons-material';
import { ICandidateStates } from 'types/jobPipeline';
import { formatLabel } from 'utilities/utils';

interface TestCandidate {
    id: string;
    name: string;
    avatar?: string;
    tier: number;
    score: number;
    appliedDate: string;
    modifiedDate: string;
    tags: ICandidateStates[];
    tasks: number;
    isFavorite: boolean;
}

interface CandidateCardProps {
    candidate: TestCandidate;
    isSelected: boolean;
    onSelect: (id: string) => void;
    isFavorite: boolean;
    onToggleFavorite: (id: string) => void;
    getTierColor: (tier: number) => string;
    isSelectionDisabled?: boolean;
}

export const CandidateCard: React.FC<CandidateCardProps> = ({
    candidate,
    isSelected,
    onSelect,
    isFavorite,
    onToggleFavorite,
    getTierColor,
    isSelectionDisabled,
}) => {
    return (
        <Card
            sx={{
                mb: 1.5,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: 'none',
                borderRadius: 1.5,
                transition: 'all 0.2s',
                '&:hover': {
                    boxShadow: 2,
                },
            }}
        >
            <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                {/* Header row - Lovable style */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DragIndicator
                            sx={{
                                color: 'text.disabled',
                                cursor: 'grab',
                                fontSize: 16,
                            }}
                        />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '10px', fontWeight: 500 }}>
                            {!isNaN(candidate.tier) && candidate.tier !== 0 && (
                                <Typography component="span" sx={{ color: '#2e7d32', fontSize: 'inherit', fontWeight: 'inherit' }}>
                                    Tier {candidate.tier}
                                </Typography>
                            )}
                            {!isNaN(candidate.tier) && candidate.tier !== 0 && candidate.score > 0 && (
                                <Typography component="span" sx={{ color: 'text.disabled', fontSize: 'inherit' }}>|</Typography>
                            )}
                            {candidate.score > 0 && (
                                <Typography component="span" sx={{ color: '#f57c00', fontSize: 'inherit', fontWeight: 'inherit' }}>
                                    AI: {candidate.score}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton
                            size="small"
                            onClick={() => onToggleFavorite(candidate.id)}
                            sx={{ p: 0.5, width: 28, height: 28 }}
                        >
                            {isFavorite ? (
                                <Star sx={{ color: '#fbbf24', fontSize: 16, fill: '#fbbf24' }} />
                            ) : (
                                <StarBorder sx={{ fontSize: 16, color: 'text.disabled' }} />
                            )}
                        </IconButton>
                        <IconButton size="small" sx={{ p: 0.5, width: 28, height: 28 }}>
                            <MoreVert sx={{ fontSize: 16, color: 'text.disabled' }} />
                        </IconButton>
                    </Box> */}
                </Box>

                {/* Candidate info row */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
                    <Tooltip
                        title={isSelectionDisabled ? "You can select up to 3 candidates for testing" : ""}
                        placement="top-start"
                        arrow
                    >
                        <Box component="span">
                            <Checkbox
                                checked={isSelected}
                                onChange={() => onSelect(candidate.id)}
                                disabled={isSelectionDisabled}
                                sx={{ p: 0, mt: 0.5 }}
                                size="small"
                            />
                        </Box>
                    </Tooltip>

                    <Avatar
                        src={candidate.avatar}
                        alt={candidate.name}
                        sx={{ width: 40, height: 40, flexShrink: 0 }}
                    >
                        {candidate.name.split(' ').map(n => n[0]).join('')}
                    </Avatar>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25, lineHeight: 1.4 }}>
                            {candidate.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                            Applied: {candidate.appliedDate}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                            Modified: {candidate.modifiedDate}
                        </Typography>
                    </Box>
                </Box>

                {/* Tags */}
                {candidate.tags.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
                        {candidate.tags.map((state, idx) => (
                            <Box
                                key={idx}
                                component="span"
                                sx={{
                                    padding: '2px 6px',
                                    borderRadius: '12px',
                                    bgcolor: !state.completed ? '#f57c00CC' : '#4caf50CC',
                                    fontSize: '8px',
                                    fontWeight: 600,
                                    color: 'black',
                                    textTransform: 'none',
                                    lineHeight: 1.5,
                                }}
                            >
                                {formatLabel(state.label)}
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Footer - Lovable style with border-top
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pt: 1.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                        {candidate.tasks === 0 ? 'No tasks' : `${candidate.tasks} tasks`}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" sx={{ p: 0.5, width: 28, height: 28 }}>
                            <ChatBubbleOutline sx={{ fontSize: 16, color: 'text.disabled' }} />
                        </IconButton>
                        <IconButton size="small" sx={{ p: 0.5, width: 28, height: 28 }}>
                            <Person sx={{ fontSize: 16, color: 'text.disabled' }} />
                        </IconButton>
                    </Box>
                </Box> */}
            </CardContent>
        </Card>
    );
};
